import { useEffect, useRef, useState } from 'react';
import {
  EMPTY_RAFFLE_DRAW_MOTION,
  RAFFLE_SELECT_MS,
  RaffleDrawMotion,
  raffleDisposalDurationMs,
} from './ticketMotion';

export function useRaffleDrawMotion({
  status,
  remainingIds,
  lastEliminatedId,
  allIds,
}: {
  status?: string | null;
  remainingIds: readonly string[];
  lastEliminatedId?: string | null;
  allIds: readonly string[];
}): RaffleDrawMotion {
  const [motion, setMotion] = useState<RaffleDrawMotion>(
    EMPTY_RAFFLE_DRAW_MOTION,
  );
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  };

  useEffect(() => {
    if (status !== 'DRAWING') {
      clearTimers();
      setMotion(EMPTY_RAFFLE_DRAW_MOTION);
      return;
    }

    const remaining = new Set(remainingIds);
    setMotion((prev) => {
      const emptiedIds = new Set(prev.emptiedIds);
      for (const id of allIds) {
        if (
          !remaining.has(id) &&
          id !== lastEliminatedId &&
          prev.selectedId !== id &&
          prev.disposingId !== id
        ) {
          emptiedIds.add(id);
        }
      }
      return { ...prev, emptiedIds };
    });
  }, [allIds, lastEliminatedId, remainingIds, status]);

  useEffect(() => {
    if (status !== 'DRAWING') {
      return;
    }
    if (!lastEliminatedId) {
      return;
    }

    clearTimers();
    setMotion((prev) => {
      if (prev.emptiedIds.has(lastEliminatedId)) {
        return prev;
      }
      const emptiedIds = new Set(prev.emptiedIds);
      if (prev.selectedId && prev.selectedId !== lastEliminatedId) {
        emptiedIds.add(prev.selectedId);
      }
      if (prev.disposingId && prev.disposingId !== lastEliminatedId) {
        emptiedIds.add(prev.disposingId);
      }
      return {
        selectedId: lastEliminatedId,
        disposingId: null,
        emptiedIds,
      };
    });

    const disposeAt = window.setTimeout(() => {
      setMotion((prev) => ({
        ...prev,
        selectedId: null,
        disposingId: lastEliminatedId,
      }));
    }, RAFFLE_SELECT_MS);

    const emptyAt = window.setTimeout(
      () => {
        setMotion((prev) => {
          const emptiedIds = new Set(prev.emptiedIds);
          emptiedIds.add(lastEliminatedId);
          return {
            selectedId: null,
            disposingId: null,
            emptiedIds,
          };
        });
      },
      RAFFLE_SELECT_MS + raffleDisposalDurationMs(lastEliminatedId),
    );

    timers.current = [disposeAt, emptyAt];
    return clearTimers;
  }, [lastEliminatedId, status]);

  return motion;
}
